/**
 * A drawn score as a sheet of paper, ready for a browser to print.
 *
 * Printing is the one thing an editor does that is not about the screen at all, and the
 * difference is the page: on a desk the music is widened to fill the window and the last
 * page stops where the music does, while on paper every page is the paper -- the size the
 * score says, the margins it says, and the blank half of the last sheet included.
 * {@link PRINT_OPTIONS} says that in the renderer's own words, and this file lays the pages
 * it produced out at their true size.
 *
 * It is a string and not a DOM, so it can be written into a frame, opened in a window, or
 * saved and printed by something that is not a browser at all. Nothing here touches
 * `document`, which is what keeps this package free of the DOM.
 */

import type { Score } from "../document/types";
import type { RenderOptions } from "./port";
import type { Rendering } from "./render";

/**
 * How a score is drawn when it is going to paper.
 *
 * The page itself is not stated: it comes from the score's own layout, which is exactly
 * what a host must stop overriding when it prints. Nor are the breaks: what a bar asks for
 * is the score's, and `render/breaks.ts` works out how to ask the engine for it -- a sheet
 * that laid out its own systems would not be the score the user was looking at. The scale
 * is the engine's natural one because the sheet sizes every page in millimetres afterwards,
 * so the pixel numbers in the SVG only have to be proportional, not right.
 */
export const PRINT_OPTIONS: RenderOptions = {
  scale: 100,
  adjustPageHeight: false,
};

/** Text going into markup, with the five characters that would end an element escaped. */
function escaped(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** A millimetre measurement as CSS wants it: no more places than a printer can hold. */
function mm(millimetres: number): string {
  return `${Math.round(millimetres * 1e4) / 1e4}mm`;
}

/**
 * The stylesheet, which is where the paper actually happens.
 *
 * `@page` states the sheet and takes the margin off it, because the music was already
 * engraved with its own margins inside the page and a second border from the browser would
 * push it off the paper. Each page is then a box of exactly that size with the engine's SVG
 * stretched over it: the SVG carries a `viewBox` of the whole page, so filling the box
 * scales the music to the sheet without distorting it.
 */
function styles(score: Score): string {
  const { pageWidth, pageHeight } = score.layout;
  return [
    `@page { size: ${mm(pageWidth)} ${mm(pageHeight)}; margin: 0; }`,
    "html, body { margin: 0; padding: 0; background: #fff; color: #000; }",
    `.page { width: ${mm(pageWidth)}; height: ${mm(pageHeight)}; overflow: hidden;`,
    "  break-after: page; page-break-after: always; }",
    ".page:last-child { break-after: auto; page-break-after: auto; }",
    ".page > svg { display: block; width: 100%; height: 100%; }",
  ].join("\n");
}

/**
 * Every drawn page as one printable document.
 *
 * The pages are the ones the renderer produced, in order, and nothing is re-engraved here:
 * a host that prints what it is looking at and a host that renders afresh for paper both
 * pass a `Rendering`, and this only lays it out.
 */
export function printSheet(score: Score, rendering: Rendering): string {
  const pages = rendering.pages
    .map((page) => `<section class="page">${page.svg}</section>`)
    .join("\n");
  return [
    "<!doctype html>",
    '<html lang="en">',
    "<head>",
    '<meta charset="utf-8">',
    `<title>${escaped(score.meta.title)}</title>`,
    `<style>\n${styles(score)}\n</style>`,
    "</head>",
    "<body>",
    pages,
    "</body>",
    "</html>",
  ].join("\n");
}
